import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Button,
    CircularProgress,
    Grid,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Paper,
    PaperProps,
    Box
} from "@mui/material";
import { useState } from "react";
import { useDataProvider, useNotify } from "react-admin";
import ApiResources from "src/providers/ApiResources";
import Draggable from 'react-draggable';
import DownloadIcon from '@mui/icons-material/Download';
import InsertLinkIcon from '@mui/icons-material/InsertLink';

interface PromoExcelModalProps {
    open: boolean;
    setOpen: any;
    onInsert?: (file: Array<File>) => void;
}

const PaperComponent = (props: PaperProps) => {
    return (
        <Draggable
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}
  
export const PromoExcelModal = (props: PromoExcelModalProps) => {
    const { open, setOpen, onInsert } = props;

    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const notify = useNotify();

    const executeCommand = async () => {
        try {
            setLoading(true);
            setSubmitError(null);

            fetch(ApiResources.postCmdPromoExcel(), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('swatim_token')}`,
                    Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
            })
            .then(response => {
                if (!response.ok) {
                  return response.text().then(text => {
                    let errorMsg = text;
                    try {
                      const json = JSON.parse(text);
                      errorMsg = json.message || JSON.stringify(json);
                    } catch (_) {}
                    return Promise.reject(new Error(errorMsg));
                  });
                }

                return Promise.all([response.blob(), Promise.resolve(response.headers)]);
            })
            .then(([blob, headers]) => {
                let fileName = `promo_${Date.now()}.xlsx`;

                setFile(new File([blob], `promo_${Date.now()}.xlsx`, {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    lastModified: Date.now(),
                }));

                notify(`File ${fileName} has been generated.`, { type: 'success' });
                setLoading(false);
            })
            .catch((error) => {
                notify(error.message, { type: 'error' });
                setSubmitError(error.message);
                setLoading(false);
            });
        } catch (e) {
            console.log(e);
        }
    }

    const handleDownload = () => {
        try {
            const url = window.URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.log(e);
        }
    }

    const handleInsert = () => {
        onInsert([file]);
    }

    return (
        <Dialog
            onClose={() => setOpen(false)}
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth={true}
            maxWidth="sm"
        >
            <DialogTitle sx={{padding: "18px"}}>
                <Typography variant="body1" sx={{fontSize: "20px", fontWeight: "500"}}>Generate Promo Excel</Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{padding: "18px !important"}}>
                <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
                    <Grid item sm={12} md={12}>
                    {file ? 
                        <Box marginY="10px" display="flex" gap="5px" alignSelf="end" justifyContent="start">
                            <Button onClick={() => handleInsert()} variant="outlined" size="small">
                                <InsertLinkIcon sx={{ fontSize: "18px"}} />
                                <Typography component="span" sx={{lineHeight: "1.75", fontSize: "14px", marginLeft: "8px", fontWeight: 600}}>Insert File</Typography>
                            </Button>
                            <Button onClick={() => handleDownload()} variant="outlined" size="small">
                                <DownloadIcon sx={{ fontSize: "18px"}} />
                                <Typography component="span" sx={{lineHeight: "1.75", fontSize: "14px", marginLeft: "8px", fontWeight: 600}}>Download File</Typography>
                            </Button>
                        </Box>
                    :
                        <Typography variant="subtitle1">Are you sure you want to execute this command?</Typography>
                    }
                    </Grid>

                    { submitError &&
                    <Grid item sm={12} md={12} sx={{display: "flex", marginTop: "18px"}}>
                        <Typography variant="subtitle2" color="error">{submitError}</Typography>
                    </Grid>                     
                    } 
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "18px"}}>
                <Button
                    variant="outlined"
                    onClick={() => {
                    setOpen(false);
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    type="submit" 
                    disabled={loading} 
                    onClick={() => executeCommand()}
                >
                    {loading && <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff", marginRight: "8px"}}/>}
                    Execute Command
                </Button>
            </DialogActions>
        </Dialog>
    );
};
